import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius, accessibility } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';

/** RefundDialog - inline partial-refund prompt with dollar amount input, "Refund full $X"
 *  shortcut, optional reason field, and confirm/cancel actions. Drops in wherever
 *  InlineConfirm was used for a refund flow. */
interface RefundDialogProps {
  readonly visible: boolean;
  /** Original payment amount in cents. */
  readonly originalCents: number;
  /** Cents already refunded against this payment (cumulative). */
  readonly alreadyRefundedCents?: number;
  /** Whether a reason input is required. Attendant flows always require one. */
  readonly requireReason?: boolean;
  /** Confirm-button label. Defaults to "Refund". */
  readonly confirmLabel?: string;
  /** Title shown above the controls. */
  readonly title?: string;
  /** Subtitle / context line — e.g. "Refund to customer?" */
  readonly subtitle?: string;
  readonly loading?: boolean;
  /** Called with cents (integer) + optional reason. Amount is guaranteed <= remaining. */
  readonly onConfirm: (amount: number, reason: string) => void;
  readonly onCancel: () => void;
}

const fmtMoney = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export default function RefundDialog({
  visible,
  originalCents,
  alreadyRefundedCents = 0,
  requireReason = false,
  confirmLabel = 'Refund',
  title,
  subtitle,
  loading,
  onConfirm,
  onCancel,
}: RefundDialogProps) {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const remainingCents = Math.max(0, originalCents - alreadyRefundedCents);
  const remainingDollars = (remainingCents / 100).toFixed(2);

  const [amountText, setAmountText] = useState<string>(remainingDollars);
  const [reason, setReason] = useState<string>('');

  // Reset text whenever the dialog re-opens or the remaining balance changes.
  React.useEffect(() => {
    if (visible) {
      setAmountText(remainingDollars);
      setReason('');
    }
  }, [visible, remainingDollars]);

  const { amountCents, error } = useMemo(() => {
    const cleaned = amountText.replace(/[^0-9.]/g, '');
    if (!cleaned) return { amountCents: 0, error: 'Enter an amount' };
    const parsed = Number(cleaned);
    if (!Number.isFinite(parsed) || parsed <= 0) return { amountCents: 0, error: 'Enter an amount' };
    const cents = Math.round(parsed * 100);
    if (cents > remainingCents) return { amountCents: cents, error: `Max refundable is ${fmtMoney(remainingCents)}` };
    return { amountCents: cents, error: null as string | null };
  }, [amountText, remainingCents]);

  const reasonError = requireReason && reason.trim().length === 0 ? 'Reason required' : null;
  const canSubmit = !loading && !error && !reasonError && amountCents > 0;

  if (!visible) return null;

  return (
    <View style={styles.container}>
      {title && <Text style={styles.title}>{title}</Text>}
      {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
      {alreadyRefundedCents > 0 && (
        <Text style={styles.context}>
          {fmtMoney(alreadyRefundedCents)} of {fmtMoney(originalCents)} already refunded · {fmtMoney(remainingCents)} remaining
        </Text>
      )}

      <View style={styles.amountRow}>
        <View style={styles.amountInputWrap}>
          <Text style={styles.dollarSign}>$</Text>
          <TextInput
            style={styles.amountInput}
            value={amountText}
            onChangeText={setAmountText}
            keyboardType="decimal-pad"
            placeholder="0.00"
            placeholderTextColor={colors.textLight}
            editable={!loading}
            accessibilityLabel="Refund amount in dollars"
          />
        </View>
        <TouchableOpacity
          style={styles.fullBtn}
          onPress={() => setAmountText(remainingDollars)}
          disabled={loading || amountCents === remainingCents}
          accessibilityRole="button"
          accessibilityLabel={`Refund full ${fmtMoney(remainingCents)}`}
        >
          <Text style={[styles.fullBtnText, amountCents === remainingCents && styles.fullBtnTextActive]}>
            Refund full {fmtMoney(remainingCents)}
          </Text>
        </TouchableOpacity>
      </View>
      {error && <Text style={styles.error}>{error}</Text>}

      {requireReason && (
        <View>
          <TextInput
            style={styles.reasonInput}
            value={reason}
            onChangeText={setReason}
            placeholder="Reason for refund (required)"
            placeholderTextColor={colors.textLight}
            maxLength={2000}
            multiline
            editable={!loading}
            accessibilityLabel="Refund reason"
          />
          {reasonError && <Text style={styles.error}>{reasonError}</Text>}
        </View>
      )}

      <View style={styles.actions}>
        <TouchableOpacity style={styles.cancelBtn} onPress={onCancel} disabled={loading}>
          <Text style={[styles.cancelText, { color: colors.textSecondary }]}>Cancel</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.confirmBtn, !canSubmit && styles.confirmBtnDisabled]}
          onPress={() => canSubmit && onConfirm(amountCents, reason.trim())}
          disabled={!canSubmit}
          accessibilityRole="button"
        >
          {loading ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <Text style={styles.confirmText}>
              {confirmLabel} {fmtMoney(amountCents || 0)}
            </Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: {
    borderRadius: borderRadius.sm,
    borderWidth: 1,
    borderColor: c.warning + '40',
    backgroundColor: c.warning + '08',
    padding: spacing.md,
    marginTop: spacing.sm,
    gap: spacing.sm,
  },
  title: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
    color: c.text,
  },
  subtitle: {
    fontSize: fontSize.sm,
    color: c.text,
  },
  context: {
    fontSize: fontSize.xs,
    color: c.textSecondary,
  },
  amountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  amountInputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: c.surface,
    borderRadius: borderRadius.sm,
    borderWidth: 1,
    borderColor: c.border,
    paddingHorizontal: spacing.sm,
    minHeight: accessibility.minTouchTarget,
    minWidth: 110,
  },
  dollarSign: {
    fontSize: fontSize.md,
    color: c.textSecondary,
    marginRight: 2,
  },
  amountInput: {
    flex: 1,
    fontSize: fontSize.md,
    fontWeight: fontWeight.medium,
    color: c.text,
    paddingVertical: spacing.sm,
    minWidth: 60,
  },
  fullBtn: {
    flexShrink: 1,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderRadius: borderRadius.sm,
    borderWidth: 1,
    borderColor: c.warning + '60',
  },
  fullBtnText: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.semibold,
    color: c.warning,
  },
  fullBtnTextActive: {
    opacity: 0.5,
  },
  reasonInput: {
    backgroundColor: c.surface,
    borderRadius: borderRadius.sm,
    borderWidth: 1,
    borderColor: c.border,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    fontSize: fontSize.sm,
    color: c.text,
    minHeight: 60,
    textAlignVertical: 'top',
  },
  error: {
    fontSize: fontSize.xs,
    color: c.danger,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  cancelBtn: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: borderRadius.sm,
    backgroundColor: c.surfaceSecondary,
  },
  cancelText: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
  },
  confirmBtn: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: borderRadius.sm,
    minWidth: 120,
    alignItems: 'center',
    backgroundColor: c.warning,
  },
  confirmBtnDisabled: {
    opacity: 0.5,
  },
  confirmText: {
    color: '#fff',
    fontSize: fontSize.sm,
    fontWeight: fontWeight.semibold,
  },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
